import {ActionIcon, Badge, Button, Group, Text, Tooltip} from '@mantine/core';
import {useParams, Link} from 'react-router-dom';
import {
  TrackedSerialNumbersService,
} from '../api/generated/TrackedSerialNumbersService';
import {
  PagedDataTableConfig,
  usePagedDataTable,
  getActiveFiltersCount,
} from '../hooks/paged-data-table/use-paged-data-table';
import {useProduce} from '../hooks/use-produce';
import {TableFilters, FilterSchema, FilterState} from './tracked-sn-listing-filter';
import {BasicPage} from '../components/basic-page';
import {BreadcrumbPart} from '../components/page-title';
import {ROUTES} from '../routes';
import {formatJSDate} from '../utils/luxon';
import {DateTime} from 'luxon';
import {useCallback, useMemo, useState} from 'react';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faFileSpreadsheet, faClockRotateLeft, faOilWell, faPenToSquare} from '@fortawesome/pro-regular-svg-icons';
import {TrackedSnAuditHistoryModal} from './tracked-sn-audit-history';
import {TrackedSnEditModal} from './tracked-sn-edit-modal';
import {PreventBubble} from '../components/prevent-bubble';
import {TrackedSerialNumberListingDto} from '../api/generated/index.defs';
import {CopyButton} from '../components/copy-button';
import {notify} from '../hooks/use-subscription';
import {useUser} from '../auth/use-auth';

const breadcrumbs: BreadcrumbPart[] = [
  {title: 'S/N Tracking', href: ROUTES.ADMIN.SERIAL_NUMBER_TRACKING.INDEX.path},
  {title: 'Tracked Serial Numbers'},
];

// Map status colors from backend to Mantine color names
const statusColorMap: Record<string, string> = {
  red: 'red',      // Scrapped
  yellow: 'yellow', // In Repair
  gray: 'gray',     // Inactive
  green: 'green',   // Active
};

// Map certification health to Mantine colors
const certificationColorMap: Record<string, string> = {
  'Within Certification': 'green',
  'COS Overdue': 'orange',
  'UECOC Overdue': 'red',
};

// Render a colored status badge (Inactive reveals its reason on hover)
const renderStatusBadge = (item: TrackedSerialNumberListingDto) => {
  const color =
    item.serviceStatusColor ? statusColorMap[item.serviceStatusColor] : "gray";
  const badge = (
    <Badge color={color} variant="filled" size="sm">
      {item.serviceStatus}
    </Badge>
  );
  if (item.serviceStatus === 'Inactive' && item.inactiveReason) {
    return (
      <Tooltip label={item.inactiveReason} multiline maw={320} withArrow>
        <span style={{cursor: 'help'}}>{badge}</span>
      </Tooltip>
    );
  }
  return badge;
};

// Render certification status badge
const renderCertificationBadge = (item: TrackedSerialNumberListingDto) => {
  if (!item.certificationStatus) return <Text size="sm" c="dimmed">-</Text>;

  const color = certificationColorMap[item.certificationStatus] ?? 'gray';
  return (
    <Badge color={color} variant="light" size="sm">
      {item.certificationStatus}
    </Badge>
  );
};

// Format date for display (handles Date objects or undefined)
const formatDate = (date: Date | string | undefined | null) => {
  if (!date) return '-';
  try {
    return formatJSDate(date, DateTime.DATE_SHORT);
  } catch {
    return '-';
  }
};

// State for audit history modal
type AuditModalState = {
  opened: boolean;
  trackedSerialNumberId: number | undefined;
  serialNumber: string | undefined;
};

// State for edit modal
type EditModalState = {
  opened: boolean;
  item: TrackedSerialNumberListingDto | undefined;
};

type ComponentState = {
  filter: FilterState;
};

export const TrackedSnListing = () => {
  const {id} = useParams<{id: string}>();
  const partnerSiteIdNum = Number(id);
  const user = useUser();
  const canManageRigs = user.role === 'Global Admin' || user.role === 'Partner Site Manager';

  // Filter state
  const [clear, setClear] = useState<boolean>(false);
  const [state, setState] = useProduce<ComponentState>({
    filter: {} as FilterState,
  });

  // Audit history modal state
  const [auditModal, setAuditModal] = useState<AuditModalState>({
    opened: false,
    trackedSerialNumberId: undefined,
    serialNumber: undefined,
  });

  // Edit modal state
  const [editModal, setEditModal] = useState<EditModalState>({
    opened: false,
    item: undefined,
  });

  const openAuditHistory = useCallback((item: TrackedSerialNumberListingDto) => {
    setAuditModal({
      opened: true,
      trackedSerialNumberId: item.id,
      serialNumber: item.serialNumber,
    });
  }, []);

  const closeAuditHistory = useCallback(() => {
    setAuditModal((prev) => ({...prev, opened: false}));
  }, []);

  const openEditModal = useCallback((item: TrackedSerialNumberListingDto) => {
    setEditModal({
      opened: true,
      item,
    });
  }, []);

  const closeEditModal = useCallback(() => {
    setEditModal((prev) => ({...prev, opened: false}));
  }, []);

  // Create a service function bound to the partnerSiteId
  const fetchListing = useCallback(
    (params: Omit<Parameters<typeof TrackedSerialNumbersService.getListing>[0], 'partnerSiteId'>) => {
      return TrackedSerialNumbersService.getListing({...params, partnerSiteId: partnerSiteIdNum});
    },
    [partnerSiteIdNum],
  );

  // Export to CSV handler - passes current filters to export the same dataset as the listing
  const onExportCsv = useCallback(async () => {
    const {data, hasErrors} = await TrackedSerialNumbersService.exportToCsv({
      partnerSiteId: partnerSiteIdNum,
      ...state.filter,
    });
    if (!hasErrors && data) {
      window.location.href = data;
    }
  }, [partnerSiteIdNum, state.filter]);

  // Filter callbacks
  const clearFilterState = useCallback(() => {
    setState((draft) => {
      draft.filter = {} as FilterState;
    });
    setClear(true);
  }, [setState]);

  const getTableFilters = useCallback(
    (dismiss: () => void) => (
      <TableFilters
        defaultValues={state.filter}
        clear={clear}
        setClear={setClear}
        onSubmit={(filters) => {
          setState((draft) => {
            Object.keys(filters).forEach(
              (key) => ((draft.filter as Record<string, unknown>)[key] = (filters as Record<string, unknown>)[key]),
            );
          });
          dismiss();
        }}
        partnerSiteId={partnerSiteIdNum}
      />
    ),
    [clear, setState, state.filter, partnerSiteIdNum],
  );

  // Render action buttons for a row
  const renderActionButtons = useCallback(
    (item: TrackedSerialNumberListingDto) => (
      <PreventBubble>
        <Group gap="xs" wrap="nowrap">
          <Tooltip label="Edit">
            <ActionIcon
              variant="subtle"
              onClick={() => openEditModal(item)}
              aria-label="Edit"
            >
              <FontAwesomeIcon icon={faPenToSquare} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label="View Audit History">
            <ActionIcon
              variant="subtle"
              onClick={() => openAuditHistory(item)}
              aria-label="View Audit History"
            >
              <FontAwesomeIcon icon={faClockRotateLeft} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </PreventBubble>
    ),
    [openAuditHistory, openEditModal],
  );

  const tableConfig = useMemo(
    () =>
      PagedDataTableConfig(fetchListing, {
        columns: [
          {
            accessor: 'serviceStatus',
            title: 'Service Status',
            render: renderStatusBadge,
            sortable: true,
          },
          {
            accessor: 'rigName',
            title: 'Rig',
            sortable: true,
            render: (item: TrackedSerialNumberListingDto) => (
              <Text size="sm" c={item.rigName ? undefined : 'dimmed'}>
                {item.rigName || 'Unassigned'}
              </Text>
            ),
          },
          {
            accessor: 'serialNumber',
            title: 'Serial Number',
            sortable: true,
            render: (item: TrackedSerialNumberListingDto) => (
              <Group gap="xs" wrap="nowrap">
                <Text size="sm">{item.serialNumber}</Text>
                <CopyButton value={item.serialNumber ?? ''} />
              </Group>
            ),
          },
          {
            accessor: 'partNumber',
            title: 'Part Number',
            sortable: true,
            render: (item: TrackedSerialNumberListingDto) => (
              <Group gap="xs" wrap="nowrap">
                <Text size="sm">{item.partNumber}</Text>
                <CopyButton value={item.partNumber ?? ''} />
              </Group>
            ),
          },
          {
            accessor: 'partDescription',
            title: 'Description',
            sortable: true,
            render: (item: TrackedSerialNumberListingDto) =>
              item.partDescription ? (
                <Tooltip label={item.partDescription} multiline maw={320} withArrow>
                  <Text size="sm" lineClamp={1} style={{maxWidth: 220, cursor: 'help'}}>
                    {item.partDescription}
                  </Text>
                </Tooltip>
              ) : (
                <Text size="sm" c="dimmed">
                  -
                </Text>
              ),
          },
          {
            accessor: 'productionDate',
            title: 'Production Date',
            sortable: true,
            render: (item: TrackedSerialNumberListingDto) =>
              formatDate(item.productionDate),
          },
          {
            accessor: 'certificationStatus',
            title: 'Certificate Status',
            render: renderCertificationBadge,
            sortable: true,
          },
          {
            accessor: 'lastServiceDate',
            title: 'Last Service Date',
            sortable: true,
            render: (item: TrackedSerialNumberListingDto) =>
              formatDate(item.lastServiceDate),
          },
          {
            accessor: 'lastServiceType',
            title: 'Last Service Type',
            sortable: true,
          },
          {
            accessor: 'nextRecommendedCosService',
            title: 'Next COS',
            sortable: true,
            render: (item: TrackedSerialNumberListingDto) =>
              formatDate(item.nextRecommendedCosService),
          },
          {
            accessor: 'nextRequiredUecoc',
            title: 'Next UECOC',
            sortable: true,
            render: (item: TrackedSerialNumberListingDto) =>
              formatDate(item.nextRequiredUecoc),
          },
          {
            accessor: 'actions',
            title: '',
            render: renderActionButtons,
            width: '80px',
          },
        ],
        defaultSort: {
          column: 'serialNumber',
          direction: 'ASC',
        },
        searchFieldNames: ['serialNumber', 'partNumber', 'partDescription', 'rigName'],
        refetchEvent: 'tracked-sn-listing',
      }),
    [fetchListing, renderActionButtons],
  );

  const runtimeConfig = useMemo(
    () => ({
      renderActions: (
        <Group gap="md">
          {canManageRigs && (
            <Link to={ROUTES.ADMIN.RIGS.INDEX.buildPath({partnerSiteId: partnerSiteIdNum})}>
              <Button variant="outline" leftSection={<FontAwesomeIcon icon={faOilWell} />}>
                Manage Rigs
              </Button>
            </Link>
          )}
          <Button variant="outline" onClick={onExportCsv} leftSection={<FontAwesomeIcon icon={faFileSpreadsheet} />}>
            Export to CSV
          </Button>
        </Group>
      ),
    }),
    [onExportCsv, partnerSiteIdNum, canManageRigs],
  );

  const pagedDataTable = usePagedDataTable(
    fetchListing,
    tableConfig,
    {
      ...runtimeConfig,
      additionalParams: state.filter,
      clearFilterState,
      renderFilter: getTableFilters,
      filterBadge: getActiveFiltersCount(state.filter),
    },
  );

  // Callback when edit modal saves successfully
  const onEditSuccess = useCallback(() => {
    closeEditModal();
    // Trigger a refetch of the listing data
    notify('tracked-sn-listing', undefined);
  }, [closeEditModal]);

  return (
    <BasicPage title={breadcrumbs}>
      {pagedDataTable}

      <TrackedSnAuditHistoryModal
        opened={auditModal.opened}
        onClose={closeAuditHistory}
        trackedSerialNumberId={auditModal.trackedSerialNumberId}
        serialNumber={auditModal.serialNumber}
      />

      <TrackedSnEditModal
        opened={editModal.opened}
        onClose={closeEditModal}
        onSuccess={onEditSuccess}
        item={editModal.item}
        partnerSiteId={partnerSiteIdNum}
      />
    </BasicPage>
  );
};
